import { useEffect, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Phone, Mail, MessageSquare, Info, ShieldCheck, CalendarPlus, FileText, PenLine, Pencil, Building2, ChevronDown, Home, MoreHorizontal, Sparkles, Star, Quote, UserPlus, UserRound, UserX, Wrench, X as XIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { displayCustomerName } from "@/lib/format";
import { Button } from "@/components/ui/Button";
import type { CustomerWithLocations, CustomerRow, LocationRow } from "./api";
import { useAddProperty, useMoveProperty } from "./api";
import { useCustomerLookup } from "@/features/purchase-orders/po";
import { AddressAutocomplete } from "./AddressAutocomplete";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  useCustomerSignals, useCustomerStatusFlag, useSetCustomerInactive,
  useCustomerMergeInfo, useUnmergeCustomer,
  useSetDoNotService, isDoNotService, useResolveSignal, type CustomerSignal } from "./api";
import { EditCustomerPanel } from "./EditCustomerPanel";
import { useEditCustomer } from "./editCustomerContext";
import { MergeCustomerModal } from "./MergeCustomerModal";
import { useCustomerMetrics, buildCustomerProse } from "./metrics";
import { differenceInMonths, parseISO } from "date-fns";
import { useCustomerAgreementRenewal, useMembershipStatus } from "@/features/agreements/api";
import { useCreateQuote } from "@/features/quotes/quotes";
import { CreateDispatchModal } from "./CreateDispatchModal";
import { Copyable, CopyCode, mailingLabel } from "@/components/Copyable";
import { TransferOwnerModal, PreviousOwnersModal } from "./CustomerOwnerTransfer";
// The pure contact-line logic (ESC grid, phone bank, best contact) lives in ./contactLines (34i87).
import { bestContactFor, buildLines, contactEraOf } from "./contactLines";
import { useContacts } from "./CustomerContacts";
import { ComposeTextModal, ComposeEmailModal } from "./CustomerMessages";
import { useLocationOwnership } from "@/features/jobs/properties";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { stripStars } from "./watchouts";
// PROPERTY-LINK-01: the house's other ESC accounts (builder / previous owner).
import { useHouseAccounts, type HouseReviewRow } from "./houseHistory";
import { HouseLinkPrompt } from "./HouseLinkPrompt";
import { HouseLinkModal } from "./HouseLinkModal";

interface Props {
  customer: CustomerWithLocations;
  selectedLocation: LocationRow | null;
  /** Kept for callers; property switching lives in the rail since 360-B. */
  onSelectLocation?: (loc: LocationRow) => void;
  /** C360-BOARD-01: the "N properties ▾" pill on the address line — the
   *  DetailView owns the switcher panel (avoids a Rail↔Header import cycle). */
  onProperties?: () => void;
  propertyPickerOpen?: boolean;
  /** NAV-360-02 (approved Exhibit A): the band owns the whole top of the
   *  page, so the DetailView hands in its pieces — the prev/next pager, the
   *  tab bar (fused flush to the band's bottom edge), and the property
   *  switcher MENU (anchored under the trigger; DetailView owns its state
   *  and content to keep the Rail↔Header import cycle broken). */
  pager?: ReactNode;
  tabs?: ReactNode;
  propertyMenu?: ReactNode;
  /** EDIT-SUITE-01: the suite's doors to a tab (Manage Plan on Membership, the portal on Comms). */
  onGoTab?: (tab: string) => void;
}

/**
 * NicknamePin — "goes by Toby" right next to the legal name. A dispatcher on a
 * live call should never have to open the Notes tab to learn what to call the
 * customer (Seth 2026-06-06: "1007902 goes by Toby — wouldn't that be nice to
 * see somewhere quickly"). Sourced from the MIG-NOTES nickname signal. A
 * confirmed nickname shows clean; an AI-proposed one carries the sparkle +
 * provenance tooltip so it reads as "found in your notes," not gospel — keep /
 * reject lives in the Watch-outs editor (360-B). Self-hides when there's no nickname.
 */
function NicknamePin({ custno }: { custno: string }) {
  const { data } = useCustomerSignals(custno);
  const nicks = (data ?? []).filter(
    (s) => s.signal_type === "nickname" && s.status !== "dismissed" && !!s.value && !s.hidden_at && !s.archived_at,
  );
  const nick = nicks.find((s) => s.status === "confirmed") ?? nicks[0];
  if (!nick) return null;
  const proposed = nick.status === "proposed";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-sm font-semibold",
        proposed ? "bg-brand-50 text-brand-700" : "bg-emerald-50 text-emerald-700",
      )}
      title={
        proposed
          ? `Pegasus found this in your notes${nick.source_text ? `: "${nick.source_text}"` : ""} — keep or reject it under Watch for → Edit`
          : "Preferred name"
      }
    >
      {proposed ? (
        <Sparkles className="h-3 w-3 text-brand-500" />
      ) : (
        <Quote className="h-3 w-3 text-emerald-500" />
      )}
      goes by {nick.value}
    </span>
  );
}

/**
 * FactsLine (360-FACTS, Seth 2026-09-29 canvas review: "Should the address be
 * a little more prominent? Is it ok not to have contact details or email
 * showing?") — the answers, made real: one quiet line of plain facts under
 * the name. Address first and bold (it's how dispatchers think about
 * accounts), then the best phone and email. Buttons act, facts inform. An
 * office correction (EDIT-360) wins its slot and carries provenance in the
 * tooltip; the rail's People block keeps the full bank.
 */
/** C360-BOARD-01: the best phone + email for the header's one-tap circles —
 *  the SAME resolution FactsLine displays (office correction wins). */

/**
 * HDR-HERO-01 (Seth 2026-10-01: "when I switch to different properties, it's
 * not showing Chris Noddings for the Jersey Ave in the header") — the
 * LOCATION's registered name, shown only when it's a real, distinct person/
 * entity: ESC's locname cleaned (Last,First flip + trailing-digits strip),
 * suppressed when it merely restates the ACCOUNT ("R&D Batteries
 * Inc-Burnsville") or the address itself. ServiceTitan keeps Customer
 * (billing) and Location (service) as separate records — this is that
 * distinction, worn lightly: account name stays the headline, the property's
 * person rides the address line.
 */
function locationPersonName(
  locname: string | null | undefined,
  accountFullname: string | null | undefined,
  add1: string | null | undefined,
): string | null {
  const raw = (locname ?? "").trim();
  if (!raw || /^\d/.test(raw)) return null; // blank, or it's really an address
  const cleaned = displayCustomerName(null, raw); // Last,First flip + id strip
  const acct = displayCustomerName(null, accountFullname ?? "");
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
  const nName = norm(cleaned);
  const nAcct = norm(acct === "Customer" ? "" : acct);
  if (!nName) return null;
  if (add1 && nName === norm(add1)) return null;
  if (nAcct && (nName === nAcct || nName.startsWith(nAcct) || nAcct.startsWith(nName))) return null;
  return cleaned;
}
/** 612-555-0134 out of whatever ESC stored. */
function prettyPhone(s: string): string {
  const d = s.replace(/\D/g, "").slice(-10);
  return d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : s;
}


export function CustomerHeader({
  customer,
  selectedLocation,
  onProperties,
  propertyPickerOpen,
  pager,
  tabs,
  propertyMenu,
  onGoTab,
}: Props) {
  const locations = customer.locations ?? [];
  // HDR-TWEAKS-01: which property you're on ("Property 2 of 4") — the
  // property-tenure line rides the band STORY line (NAV-360-04), so
  // the sticky band stays slim.
  const activeLocations = locations.filter((l) => !l.location_inactive);
  const effLocations = activeLocations.length ? activeLocations : locations;
  const propertyIndex = selectedLocation
    ? effLocations.findIndex((l) => l.locno === selectedLocation.locno) + 1
    : 0;
  // C360-BOARD-01: one-tap Call / Text / Email circles read the same best
  // phone + email the meta line shows (office correction wins).
  const [textOpen, setTextOpen] = useState(false);
  const [emailComposeOpen, setEmailComposeOpen] = useState(false);

  // RECORD-READ-MODEL (34i97): `customer.fullname` already IS the shown name
  // (useCustomer merges the office correction) — one answer everywhere.
  const displayName = displayCustomerName(null, customer.fullname);

  // "Agreement active" badge — DERIVED, not a stored flag. True only when an
  // active-flagged agreement exists AND hasn't passed its expire date (a
  // sa_inactive=0 record past its expiry is the stale-flag trap from Julie
  // Achterkirch, per [[feedback_derive_dont_flag]]). Reuses the same query the
  // attention strip runs (React Query dedupes), so no extra fetch.
  const { data: renewal } = useCustomerAgreementRenewal(customer.custno);
  const agreementActive = !!renewal && renewal.days_left >= 0;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [scheduleOpen, setScheduleOpen] = useState(false);
  // QUOTE-FROM-360 (Seth 2026-08-18): the header's Quote button goes live —
  // one click creates an install proposal for this customer + opens the
  // Best/Better/Good builder.
  const createQuote = useCreateQuote();
  // The recorded owner for the selected home — when a transfer has happened, the
  // new owner (a Pegasus-native identity) supersedes the ESC-mirrored name up top.
  const [transferOpen, setTransferOpen] = useState(false);
  const [prevOpen, setPrevOpen] = useState(false);
  const [editMenuOpen, setEditMenuOpen] = useState(false);
  const { data: owned } = useLocationOwnership(customer.custno, selectedLocation?.locno ?? null);
  const currentOwner = owned?.currentOwner ?? null;
  // The lines we call today — frozen onto the outgoing owner when a sale is recorded.
  const transferContacts = useContacts(customer.custno, selectedLocation?.locno ?? null).data ?? [];
  // INACTIVE-OVERLAY (Seth 2026-08-13): the Pegasus-native inactive mark — the
  // "ESC won't merge this duplicate, keep dispatchers off it" workflow. Search
  // hides flagged accounts by default; this header is where it's set/cleared.
  const { data: statusFlag } = useCustomerStatusFlag(customer.custno);
  const setInactive = useSetCustomerInactive();
  const pegasusInactive = statusFlag?.inactive === true;
  const [markOpen, setMarkOpen] = useState(false);
  const [markReason, setMarkReason] = useState("");
  // DNS-01 (Seth 2026-08-21): ESC "bad credit rating" = do not service.
  // One derived answer: the Pegasus mark OR a BAD-style ESC rating.
  const setDns = useSetDoNotService();
  const dnsActive = isDoNotService(statusFlag, customer.credit_rating);
  const dnsFromEsc = !statusFlag?.do_not_service && dnsActive;
  const [dnsOpen, setDnsOpen] = useState(false);
  const [dnsReason, setDnsReason] = useState("");
  // EDIT-360 + MERGE-360 modals + this account's merge state.
  // EDIT-SUITE-01 (34i97): "Edit Customer" is ONE suite opened from every pencil on
  // the page (the provider lives on the 360); the ··· item and the E key open it here.
  const edit = useEditCustomer();
  const [addPropOpen, setAddPropOpen] = useState(false);
  const [movingLoc, setMovingLoc] = useState<LocationRow | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "e" || e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      if (document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      edit.open("account");
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [edit]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const { data: mergeInfo } = useCustomerMergeInfo(customer.custno);
  const unmerge = useUnmergeCustomer();
  // PERM: merge is sensitive (admin-only until granted); mark-inactive is
  // normal office hygiene (office + dispatcher by default).
  const canMerge = useCan("customers.merge");
  const canMarkInactive = useCan("customers.mark_inactive");
  const canTransferOwner = useCan("customers.transfer_owner");
  // TECH-PREF-CONFIRM: who may answer a badge that came from the notes (same gate as Watch For).
  const canCurate = useCan("customers.edit");

  // NAV-360-02 — the band's working facts. One contact resolution feeds the
  // circles AND the meta line; the trigger carries the address + whose name
  // is on this property's door (HDR-HERO-01's Chris-on-Jersey rule, kept).
  // NEW-OWNER-CONTACTS: after a recorded sale the circles dial the NEW owner,
  // not the numbers ESC still carries for the previous one.
  const contact = bestContactFor(customer, selectedLocation, transferContacts, contactEraOf(owned));
  const shownName = currentOwner?.name ?? displayName;
  // COPY-ANYWHERE on the address (Seth 2026-10-20: "we lost the ability to
  // right click on address and it copy the customer name, address, etc" — the
  // picker became a button in NAV-360-06 and dropped the suit). Right-click
  // the address in either band → the mailing block: name / street / city, ST zip.
  const mailBlock = mailingLabel({
    name: shownName,
    address: selectedLocation?.add1 ?? null,
    city: selectedLocation?.city ?? null,
    state: selectedLocation?.state ?? null,
    zip: selectedLocation?.zip ?? null,
  });
  const initials =
    shownName
      .split(/[\s,]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("") || "?";
  const person = locationPersonName(selectedLocation?.locname, customer.fullname, selectedLocation?.add1);
  // 360-POLISH-03 (Seth 2026-10-13: "the city needs to be much larger font —
  // match the street") + HDR-DISPATCH-01 (2026-10-20: "make sure street and
  // city is still prominent in the picker"): line 2 of the picker is the
  // CITY, ST — always — then whose name is on this door when it differs
  // from the account's, then the property count, small.
  const subCity = selectedLocation?.city
    ? `${selectedLocation.city}${selectedLocation.state ? `, ${selectedLocation.state}` : ""}`
    : null;
  const subCount =
    effLocations.length > 1
      ? propertyIndex > 0
        ? `${propertyIndex} of ${effLocations.length} properties`
        : `${effLocations.length} properties`
      : "1 property";
  // NAV-360-05 (Seth: "put recent buyer … next to the member badge"): the
  // prose is built HERE now — the chimera sentence becomes a badge in the
  // name row; the ordinary relationship line stays on the story line.
  const { data: custMetrics } = useCustomerMetrics(customer.custno);
  const prose = buildCustomerProse({ metrics: custMetrics ?? null, locationCount: locations.length });
  // HDR-DISPATCH-01 badges — derived, never typed.
  // PLAN-TERM-BADGE (34i83, Seth: "PLUS 3 should be 3 Year Plan … confirm this
  // pulls from the length of the plan they are on so Pegasus plans display the
  // same"): the badge is the plan's TERM, from pegasus_membership_status — the
  // one lifecycle rule every screen uses — which reads the ESC agreement's
  // contract period ("3 Year") or a Pegasus-sold plan's term alike. So a
  // 3-year plan sold in Pegasus next spring wears the same "3-Year Plan" badge
  // as a 3-year ESC agreement today. ESC's location class (custom_1 "PLUS 3")
  // is not used for the badge any more — it lags the real agreement and has no
  // Pegasus equivalent; it still names commercial sites below.
  const { data: membership } = useMembershipStatus(customer.custno);
  const planLive = !!membership && (membership.status === "active" || membership.status === "expiring" || membership.status === "ended");
  const planLabel = membership?.term_years
    ? `${membership.term_years}-Year Plan`
    : renewal?.agrmt_no === "PLUS (Pegasus)" ? "Plan" : "Member";
  const fmtDay = (iso: string | null | undefined) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null);
  const planTitle = membership && planLive
    ? [
        membership.plan_name ?? planLabel,
        membership.source === "pegasus" ? "sold in Pegasus" : `agreement ${membership.plan_id}`,
        membership.status === "ended"
          ? `term ended ${fmtDay(membership.expire_date) ?? ""} · ${membership.checks_owed} tune-up${membership.checks_owed === 1 ? "" : "s"} still owed`
          : `renews ${fmtDay(membership.expire_date) ?? ""}${membership.days_to_expire != null && membership.days_to_expire <= 60 ? ` · ${membership.days_to_expire} days left` : ""}`,
      ].join(" · ")
    : renewal
      ? `${renewal.agrmt_no === "PLUS (Pegasus)" ? "Plan sold in Pegasus" : `Agreement ${renewal.agrmt_no}`} · renews ${fmtDay(renewal.expire_date) ?? ""}`
      : undefined;
  const escClass = (selectedLocation?.custom_1 ?? locations.find((l) => (l.custom_1 ?? "").trim())?.custom_1 ?? "").trim().toUpperCase();
  const isCommercial = escClass === "COMMERCIAL" || escClass === "COMM PLUS";
  // Stated tech preference ("Brent only", "Not AJ") — 531 of them sit in the
  // notes as extracted signals; confirmed ones read solid, proposed ones dashed.
  const { data: custSignals } = useCustomerSignals(customer.custno);
  // (Three of the 531 carry "Do Not Service" as the value — a DNS note the
  // extractor filed under the wrong type; that is the rose badge's job.)
  const techPref = (custSignals ?? [])
    .filter((x) => x.signal_type === "tech_pref" && !x.archived_at && !x.hidden_at && (x.status === "confirmed" || x.status === "proposed") && !!x.value?.trim() && !/do not service|\bdns\b/i.test(x.value))
    .sort((a, b) => (a.status === "confirmed" ? 0 : 1) - (b.status === "confirmed" ? 0 : 1) || (Number(b.confidence ?? 0) - Number(a.confidence ?? 0)))[0] ?? null;
  const techExclude = !!techPref && (!!(techPref.detail as { exclude?: boolean } | null)?.exclude || /^(not|no|avoid|don'?t|do not)\b/i.test(techPref.value));
  // TECH-PREF-CONFIRM: VIP rides the same rule as the tech request — dashed
  // while it is only the notes' word, solid once the office confirms it.
  const vipSignal = (custSignals ?? [])
    .filter((x) => x.signal_type === "vip" && !x.archived_at && !x.hidden_at && (x.status === "confirmed" || x.status === "proposed"))
    .sort((a, b) => (a.status === "confirmed" ? 0 : 1) - (b.status === "confirmed" ? 0 : 1))[0] ?? null;
  // "When do new buyers fall off?" (Seth 2026-10-12): the chimera flag is
  // re-derived from the OLD ESC records on every metrics run (a prior-owner
  // note in the location notes, or the ledger being 2+ years older than the
  // customer row) — so by itself it never expires. The badge therefore
  // self-retires once Pegasus KNOWS the handoff and it is older than
  // RECENT_BUYER_MONTHS; with no recorded/derived handoff it stays until
  // per-owner metrics ship (BUILD-17 / DEC-NEW-04).
  const RECENT_BUYER_MONTHS = 24;
  // PROPERTY-LINK-01 (Seth 2026-10-16, 300 Lake St W): the house's other ESC
  // accounts with visits — the builder before the homeowner, the previous
  // owner — read right on the address, with a link to their account.
  const houseAccounts = useHouseAccounts(customer.custno, selectedLocation?.locno ?? null).data ?? [];
  const houseOthers = houseAccounts.filter((a) => !a.isSelf && a.visitCount > 0);
  const houseSelf = houseAccounts.find((a) => a.isSelf) ?? null;
  // HOUSE-MANUAL-01: link a house by hand (··· menu). The unlink of a recorded
  // link moved to the Property Timeline card with HDR-DISPATCH-01.
  const [houseLinkOpen, setHouseLinkOpen] = useState(false);
  // The question, the morning it appears: this account is the NEWER side of a
  // derived (unconfirmed) match. Answered once, the link is a recorded fact and
  // the strip is gone for good; "different unit" / "same person" unlink the pair.
  const houseQuestion: HouseReviewRow | null = (() => {
    const o = houseOthers.find((a) => a.source === "derived" && (a.relation === "before" || a.relation === "overlap"));
    if (!o || !houseSelf || houseSelf.source === "recorded") return null;
    if (o.relation === "before" && houseSelf.visitCount > 3) return null; // an old account of ours with an older neighbour — the review list's business, not a banner
    return {
      custno: customer.custno, locno: selectedLocation?.locno ?? null, name: shownName,
      address: selectedLocation?.add1 ?? null, city: selectedLocation?.city ?? null,
      appeared: null, self_visits: houseSelf.visitCount,
      other_custno: o.custno, other_locno: o.locno, other_name: o.name, other_is_company: o.isCompany,
      other_first: o.firstService, other_last: o.lastService, other_visits: o.visitCount,
      relation: o.relation === "overlap" ? "overlap" : "before",
    };
  })();
  const ownerStartISO =
    (owned?.ownership.find((r) => !r.end_date && r.role === "owner")
      ?? owned?.ownership.find((r) => !r.end_date && r.role !== "builder"))?.start_date ?? null;
  const recentBuyer =
    !!prose?.isChimera &&
    !(ownerStartISO && differenceInMonths(new Date(), parseISO(ownerStartISO)) >= RECENT_BUYER_MONTHS);

  // The action cluster — Call · Text · Email · Book a Call · ··· · pager —
  // rendered once, used by both the full band and the condensed band (HDR-CONDENSE-01).
  const actionsCluster = (
    <>
          {/* NAV-360-04 (Seth 2026-10-12: "the text them, email them — might
              be nice icons like the book a call icon button"): Call · Text ·
              Email as proper bordered icon buttons in the Book-a-Call row —
              buttons ACT, tooltips carry the number/email, and the full
              contact bank informs from the Overview's People card. Then the
              one primary, ···, and the pager. */}
          <div className="order-2 ml-auto flex shrink-0 items-center gap-2 @5xl:order-4">
            {textOpen && contact.phone && <ComposeTextModal custno={customer.custno} initialTo={contact.phone} onClose={() => setTextOpen(false)} />}
            {emailComposeOpen && contact.email && <ComposeEmailModal custno={customer.custno} initialTo={contact.email} onClose={() => setEmailComposeOpen(false)} />}
            {(() => {
              const iconBtn =
                "hidden h-9 w-9 place-items-center rounded-lg border border-hairline bg-white text-slate-600 shadow-e1 transition-colors hover:border-brand-300 hover:bg-brand-50/40 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 sm:grid";
              return (
                <>
                  {contact.phone && (
                    <a
                      href={`tel:${contact.phone.replace(/[^0-9+]/g, "")}`}
                      title={`Call ${prettyPhone(contact.phone)}`}
                      className={iconBtn}
                    >
                      <Phone className="h-4 w-4" />
                    </a>
                  )}
                  {contact.phone && (
                    <button type="button" onClick={() => setTextOpen(true)} title={`Text ${prettyPhone(contact.phone)} from Pegasus`} className={iconBtn}>
                      <MessageSquare className="h-4 w-4" />
                    </button>
                  )}
                  {contact.email && (
                    <button type="button" onClick={() => setEmailComposeOpen(true)} title={`Email ${contact.email} from Pegasus`} className={iconBtn}>
                      <Mail className="h-4 w-4" />
                    </button>
                  )}
                </>
              );
            })()}
            <Button variant="primary" size="sm" onClick={() => setScheduleOpen(true)}>
              <CalendarPlus className="h-4 w-4" />
              Book a Call
            </Button>
            <div className="relative">
              <button
                type="button"
                onClick={() => setEditMenuOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={editMenuOpen}
                aria-label="More actions"
                title="More actions"
                className="grid h-9 w-9 place-items-center rounded-lg border border-hairline bg-white text-slate-500 shadow-e1 transition-colors hover:bg-slate-50 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
              >
                <MoreHorizontal className="h-[18px] w-[18px]" />
              </button>
              {editMenuOpen && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setEditMenuOpen(false)} />
                  <div role="menu" data-glass="panel" className="absolute right-0 z-30 mt-1 w-64 overflow-hidden rounded-xl py-1">
                    <div className="px-3 pb-0.5 pt-1.5 text-[10px] font-extrabold uppercase tracking-[0.08em] text-slate-400">Create</div>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setEditMenuOpen(false);
                        createQuote.mutate(
                          { custno: customer.custno, locno: selectedLocation?.locno ?? null, quote_type: "install" },
                          { onSuccess: (quoteId) => navigate(`/quotes/${quoteId}`) },
                        );
                      }}
                      className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50"
                    >
                      <FileText className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" />
                      <span>
                        <span className="block text-xs font-medium text-slate-800">{createQuote.isPending ? "Opening…" : "System Proposal"}</span>
                        <span className="block text-[11px] leading-snug text-slate-400">Install quote — Best/Better/Good builder with sizing + packages</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setEditMenuOpen(false);
                        createQuote.mutate(
                          { custno: customer.custno, locno: selectedLocation?.locno ?? null, quote_type: "service", title: "Repair options" },
                          { onSuccess: (quoteId) => navigate(`/quotes/${quoteId}`) },
                        );
                      }}
                      className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50"
                    >
                      <Wrench className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" />
                      <span>
                        <span className="block text-xs font-medium text-slate-800">Repair Quote</span>
                        <span className="block text-[11px] leading-snug text-slate-400">Parts-first repair ticket — service, not replacement</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setEditMenuOpen(false);
                        // 360-AUDIT #21: preserve q/from/loc so back-to-search
                        // and prev/next keep working after a quick note.
                        const next = new URLSearchParams(searchParams);
                        next.set("tab", "notes");
                        setSearchParams(next, { replace: false });
                      }}
                      className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50"
                    >
                      <PenLine className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" />
                      <span>
                        <span className="block text-xs font-medium text-slate-800">Note</span>
                        <span className="block text-[11px] leading-snug text-slate-400">Jump to Notes with the composer ready</span>
                      </span>
                    </button>
                    {/* Reach section retired (NAV-360-04): Text & Email are
                        proper icon buttons beside Book a Call now. */}
                    <div className="px-3 pb-0.5 pt-2 text-[10px] font-extrabold uppercase tracking-[0.08em] text-slate-400">Account</div>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setEditMenuOpen(false); edit.open("account"); }}
                      className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50"
                    >
                      <Pencil className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" />
                      <span>
                        <span className="block text-xs font-medium text-slate-800">Edit Customer <kbd className="ml-1 rounded border border-hairline bg-slate-50 px-1 font-mono text-[10px] text-slate-400">E</kbd></span>
                        <span className="block text-[11px] leading-snug text-slate-400">Name · people &amp; lines · reach · properties · watch for · equipment · plan · notes · links</span>
                      </span>
                    </button>
                    {canTransferOwner && (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => { setEditMenuOpen(false); setTransferOpen(true); }}
                        className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50"
                      >
                        <UserPlus className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" />
                        <span>
                          <span className="block text-xs font-medium text-slate-800">New owner</span>
                          <span className="block text-[11px] leading-snug text-slate-400">Home sold — transfer the account, archive the old owner (viewable), carry equipment &amp; history</span>
                        </span>
                      </button>
                    )}
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setEditMenuOpen(false); setHouseLinkOpen(true); }}
                      className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50"
                    >
                      <Home className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" />
                      <span>
                        <span className="block text-xs font-medium text-slate-800">Link another account to this house</span>
                        <span className="block text-[11px] leading-snug text-slate-400">The builder or previous owner is under another ESC account — show their visits and units here. Nothing moves.</span>
                      </span>
                    </button>
                    {canMerge && !mergeInfo?.mergedInto && (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => { setEditMenuOpen(false); setMergeOpen(true); }}
                        className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50"
                      >
                        <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-violet-500" />
                        <span>
                          <span className="block text-xs font-medium text-slate-800">Merge into another account</span>
                          <span className="block text-[11px] leading-snug text-slate-400">Duplicate record — move everything to the real account, hide this one. Reversible.</span>
                        </span>
                      </button>
                    )}
                    {canMarkInactive && (pegasusInactive ? (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => { setEditMenuOpen(false); setInactive.mutate({ custno: customer.custno, inactive: false }); }}
                        className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50"
                      >
                        <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                        <span>
                          <span className="block text-xs font-medium text-slate-800">Reactivate account</span>
                          <span className="block text-[11px] leading-snug text-slate-400">Shows in search again</span>
                        </span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => { setEditMenuOpen(false); setMarkReason(""); setMarkOpen(true); }}
                        className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50"
                      >
                        <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                        <span>
                          <span className="block text-xs font-medium text-slate-800">Mark account inactive</span>
                          <span className="block text-[11px] leading-snug text-slate-400">Hides it from search so nobody books onto it (duplicate / dead record). Reversible.</span>
                        </span>
                      </button>
                    ))}
                    {canMarkInactive && (statusFlag?.do_not_service ? (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => { setEditMenuOpen(false); setDns.mutate({ custno: customer.custno, on: false }); }}
                        className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50"
                      >
                        <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                        <span>
                          <span className="block text-xs font-medium text-slate-800">Clear Do Not Service</span>
                          <span className="block text-[11px] leading-snug text-slate-400">Booking works again for this account</span>
                        </span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => { setEditMenuOpen(false); setDnsReason(""); setDnsOpen(true); }}
                        className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50"
                      >
                        <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-rose-400" />
                        <span>
                          <span className="block text-xs font-medium text-slate-800">Mark Do Not Service</span>
                          <span className="block text-[11px] leading-snug text-slate-400">
                            {dnsFromEsc
                              ? "Already reads Do Not Service from the ESC credit rating — marking here makes it Pegasus-official"
                              : "Blocks new dispatches on this account (the ESC-era “bad credit rating” move). Reversible."}
                          </span>
                        </span>
                      </button>
                    ))}
                    {(owned?.previousOwners ?? []).length > 0 && (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => { setEditMenuOpen(false); setPrevOpen(true); }}
                        className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50"
                      >
                        <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                        <span>
                          <span className="block text-xs font-medium text-slate-800">Previous owners</span>
                          <span className="block text-[11px] leading-snug text-slate-400">Who had this property before — history stays attributed</span>
                        </span>
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
            {pager}
          </div>
          {scheduleOpen && (
            <CreateDispatchModal
              custno={customer.custno}
              locno={selectedLocation?.locno ?? null}
              customerName={shownName}
              address={selectedLocation?.add1}
              city={selectedLocation?.city}
              phone={contact.phone ?? selectedLocation?.phone1}
              onClose={() => setScheduleOpen(false)}
            />
          )}
    </>
  );

  return (
    <div className="space-y-4">
      {/* MERGE-360: a merged duplicate announces itself + points at the keeper. */}
      {mergeInfo?.mergedInto && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
          <div className="text-sm text-amber-800">
            <span className="font-semibold">This account was merged</span> into{" "}
            <button type="button" onClick={() => navigate(`/customers/${mergeInfo.mergedInto!.custno}`)} className="font-semibold underline underline-offset-2 hover:text-amber-900">
              #{mergeInfo.mergedInto.custno}
            </button>
            {mergeInfo.mergedInto.reason ? <> — {mergeInfo.mergedInto.reason}</> : null}. Its records live there now.
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="primary" size="sm" onClick={() => navigate(`/customers/${mergeInfo.mergedInto!.custno}`)}>
              Open the primary
            </Button>
            <Button
              variant="secondary"
              size="sm"
              loading={unmerge.isPending}
              onClick={() => { if (window.confirm("Undo this merge? Every moved record returns to this account.")) unmerge.mutate(customer.custno); }}
            >
              Undo merge
            </Button>
          </div>
        </div>
      )}
      {/* PROPERTY-LINK-01: a known address, a new account — one question. */}
      {houseQuestion && <HouseLinkPrompt row={houseQuestion} />}
      {houseLinkOpen && (
        <HouseLinkModal
          custno={customer.custno}
          locno={selectedLocation?.locno ?? null}
          address={selectedLocation?.add1 ?? null}
          customerName={shownName}
          onClose={() => setHouseLinkOpen(false)}
        />
      )}
      <EditCustomerPanel
        customer={customer}
        selectedLocation={selectedLocation}
        open={edit.section !== null}
        section={edit.section}
        onClose={edit.close}
        onGoTab={onGoTab}
        onLinkAction={(kind, loc) => {
          if (kind === "transfer") setTransferOpen(true);
          else if (kind === "link") setHouseLinkOpen(true);
          else if (kind === "merge") setMergeOpen(true);
          else if (kind === "previous") setPrevOpen(true);
          else if (kind === "addProperty") setAddPropOpen(true);
          else if (kind === "moveProperty" && loc) setMovingLoc(loc);
        }}
      />
      {addPropOpen && <AddPropertyModal custno={customer.custno} onClose={() => setAddPropOpen(false)} />}
      {movingLoc && <MovePropertyModal loc={movingLoc} onClose={() => setMovingLoc(null)} />}
      {mergeOpen && <MergeCustomerModal customer={customer} onClose={() => setMergeOpen(false)} />}
      {transferOpen && (
        <TransferOwnerModal
          custno={customer.custno}
          locno={selectedLocation?.locno ?? null}
          oldOwnerName={currentOwner?.name ?? displayName}
          oldOwnerLines={buildLines(customer, selectedLocation, transferContacts, contactEraOf(owned))}
          onClose={() => setTransferOpen(false)}
          onDone={() => setTransferOpen(false)}
        />
      )}
      {prevOpen && (
        <PreviousOwnersModal
          previousOwners={owned?.previousOwners ?? []}
          onClose={() => setPrevOpen(false)}
        />
      )}
      {/* Mark-inactive confirm — a reason keeps the audit trail honest
          ("duplicate of 1007902", "moved away", …). */}
      {markOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setMarkOpen(false)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-slate-900">Mark this account inactive?</h3>
            <p className="mt-1 text-sm text-slate-500">
              It disappears from customer search (dispatchers can&apos;t book onto it) but stays
              viewable at its direct link. Reversible any time from this menu.
            </p>
            <input
              type="text"
              autoFocus
              value={markReason}
              onChange={(e) => setMarkReason(e.target.value)}
              placeholder="Reason — e.g. duplicate of 1007902"
              className="mt-3 w-full rounded-lg border border-hairline px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setMarkOpen(false)}>Cancel</Button>
              <Button
                variant="primary"
                size="sm"
                loading={setInactive.isPending}
                onClick={() =>
                  setInactive.mutate(
                    { custno: customer.custno, inactive: true, reason: markReason.trim() || null },
                    { onSuccess: () => setMarkOpen(false) },
                  )
                }
              >
                Mark inactive
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* DNS-01 confirm — the reason shows on the badge tooltip + booking block. */}
      {dnsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setDnsOpen(false)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-slate-900">Mark Do Not Service?</h3>
            <p className="mt-1 text-sm text-slate-500">
              A red badge goes on the account and dispatchers can&apos;t book new
              dispatches onto it. History stays viewable. Reversible any time from this menu.
            </p>
            <input
              type="text"
              autoFocus
              value={dnsReason}
              onChange={(e) => setDnsReason(e.target.value)}
              placeholder="Reason — e.g. unpaid balance since 2024, abusive to techs"
              className="mt-3 w-full rounded-lg border border-hairline px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setDnsOpen(false)}>Cancel</Button>
              <Button
                variant="primary"
                size="sm"
                loading={setDns.isPending}
                onClick={() =>
                  setDns.mutate(
                    { custno: customer.custno, on: true, reason: dnsReason.trim() || null },
                    { onSuccess: () => setDnsOpen(false) },
                  )
                }
              >
                Mark Do Not Service
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* HDR-DISPATCH-01 (Seth 2026-10-20, "B is the header, always"): ONE compact
          band, all the time — tile · name · # · the badges · a real address
          picker (street + city prominent) · the actions — then the tab row.
          The property-picker row, the meta line and the story line of the
          tall band are gone: who's here lives in the People card, the
          house's other accounts and their unlink in the Property Timeline,
          the tenure/visits/lifetime facts in the tiles and the Snapshot.
          The blur lives on a childless skin layer, NOT on this container —
          backdrop-filter makes an element the containing block for every
          fixed-position descendant (the account modals render `fixed` from
          inside this tree).
          ORDER (34i83, Seth: "Name, customer number, then address picker,
          then badges — in that order from left to right"): tile · name · # ·
          the picker · the badges · the actions.
          WIDTH (a container query on the band, not the viewport — the rail
          open or closed changes what fits): from 1024px up it is one row —
          the picker holds a steady 300px and the badge box soaks up the
          slack, wrapping its badges inside itself before anything else
          moves. Narrower (a 1180 laptop with the rail open gives the band
          ~800px) it is two rows: name · # · actions, then the picker (grows)
          with the badges beside it — never dumped under the avatar. */}
      <div className="@container relative rounded-2xl border border-hairline shadow-e2">
        <div aria-hidden className="absolute inset-0 rounded-2xl bg-white/85 backdrop-blur-md" />
        <div className="relative px-4 pt-2.5 sm:px-5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            {/* identity: tile · name · # */}
            <div className="order-1 flex min-w-0 shrink-0 items-center gap-2.5">
              <span aria-hidden className="flex h-9 w-9 shrink-0 select-none items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-800 text-[13px] font-extrabold tracking-tight text-white shadow-e1 ring-1 ring-brand-900/10">
                {initials}
              </span>
              <div className="flex min-w-0 items-center gap-x-2">
                <h2 className="min-w-0 max-w-[min(36vw,360px)] select-text truncate text-[19px] font-bold leading-tight tracking-tight text-slate-900">
                  <Copyable text={shownName} label="Copy name">{shownName}</Copyable>
                </h2>
                <CopyCode value={customer.custno} label="Copy customer number" className="text-slate-500" />
                <NicknamePin custno={customer.custno} />
              </div>
            </div>

            {/* THE ADDRESS PICKER — a real control, street and city prominent
                (Seth: "the location picker still needs to be the address").
                Right-click copies name + address (COPY-ANYWHERE). */}
            {(onProperties || selectedLocation?.add1) && (
              <div className="relative order-3 min-w-0 flex-[1_1_300px] @5xl:order-2 @5xl:flex-[0_1_300px]">
                {onProperties ? (
                <Copyable bare text={mailBlock} label="Copy name + address">
                <button
                  type="button"
                  onClick={onProperties}
                  title={effLocations.length > 1 ? "Switch which property you're viewing — every tab follows it · right-click copies name + address" : "This property — add another under this customer · right-click copies name + address"}
                  className={cn(
                    "flex w-full max-w-[520px] items-center gap-3 rounded-xl border p-1.5 pr-3 text-left transition-all duration-150 ease-out-quart focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
                    propertyPickerOpen
                      ? "border-brand-300 bg-brand-100/70 shadow-e2"
                      : "border-brand-200/70 bg-brand-50/70 hover:border-brand-300 hover:shadow-e1",
                  )}
                >
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white text-brand-600 shadow-e1 ring-1 ring-brand-200/60">
                    <Home className="h-[18px] w-[18px]" strokeWidth={1.9} />
                  </span>
                  <span className="min-w-0 flex-1 leading-tight">
                    <span className="block truncate text-[14.5px] font-bold text-slate-800">
                      {selectedLocation?.add1 ?? selectedLocation?.locname ?? "No service address"}
                    </span>
                    <span className="mt-px block truncate">
                      {subCity && <span className="text-[13px] font-semibold text-slate-600">{subCity}</span>}
                      {subCity && <span className="text-[12px] text-slate-400"> · </span>}
                      {person && <span className="text-[12px] font-medium text-slate-500" title="The name on this property's door">{person} · </span>}
                      <span className="text-[12px] text-slate-500">{subCount}</span>
                    </span>
                  </span>
                  <ChevronDown className={cn("h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200", propertyPickerOpen && "rotate-180")} />
                </button>
                </Copyable>
                ) : (
                <Copyable bare text={mailBlock} label="Copy name + address">
                  <span className="truncate text-[14px] font-semibold text-slate-700">{selectedLocation!.add1}{selectedLocation!.city ? <span className="font-medium text-slate-400">, {selectedLocation!.city}</span> : null}</span>
                </Copyable>
                )}
                {propertyMenu}
              </div>
            )}

            {/* THE BADGES — derived facts in a fixed order (the mock's vocabulary C):
                Do Not Service · the plan's term · Commercial · {tech} · VIP ·
                Recent buyer · Inactive. "Owes $" waits on the past-due rule.
                The box takes the row's slack (flex-1) and wraps inside itself. */}
            <div className="order-4 flex min-w-0 flex-[0_1_auto] flex-wrap items-center gap-x-2 gap-y-1 empty:hidden @5xl:order-3 @5xl:flex-[1_1_120px]">
                {dnsActive && (
                  <span title={statusFlag?.dns_reason ?? (customer.credit_rating ? `ESC credit rating: ${customer.credit_rating}` : undefined)}>
                    <IdentityBadge tone="rose">Do Not Service</IdentityBadge>
                  </span>
                )}
                {(planLive || (membership === null && agreementActive)) && (
                  <span title={planTitle}>
                    <IdentityBadge tone={membership?.status === "ended" ? "amber" : "emerald"} icon={ShieldCheck}>{planLabel}</IdentityBadge>
                  </span>
                )}
                {isCommercial && (
                  <span title={`ESC classes this location ${escClass}: commercial fee schedule, PO numbers, business hours`}>
                    <IdentityBadge tone="slate" icon={Building2}>Commercial</IdentityBadge>
                  </span>
                )}
                {techPref && (
                  <SignalBadge signal={techPref} tone="violet" icon={techExclude ? UserX : Wrench} label={stripStars(techPref.value)} kind={techExclude ? "Don't send" : "Tech request"} canCurate={canCurate} />
                )}
                {vipSignal && (
                  <SignalBadge signal={vipSignal} tone="amber" icon={Star} label="VIP" kind="VIP" detailText={vipSignal.value && !/^vip$/i.test(vipSignal.value) ? stripStars(vipSignal.value) : null} canCurate={canCurate} />
                )}
                {recentBuyer && (
                  <span
                    title={`From the old ESC records (a prior-owner note / the account's ledger predating this customer) — history and counts here may include the previous owner. ${ownerStartISO ? `Handoff on record: the badge drops ${RECENT_BUYER_MONTHS} months after it.` : "No handoff date on record, so it stays until per-owner history ships."}`}
                  >
                    <IdentityBadge tone="amber" icon={Info}>Recent buyer</IdentityBadge>
                  </span>
                )}
                {customer.customerinactive && <IdentityBadge tone="slate">Inactive</IdentityBadge>}
                {pegasusInactive && !customer.customerinactive && (
                  <span title={statusFlag?.reason ? `Marked inactive: ${statusFlag.reason}` : "Marked inactive in Pegasus — hidden from search"}>
                    <IdentityBadge tone="slate">Inactive</IdentityBadge>
                  </span>
                )}
                {(mergeInfo?.duplicates.length ?? 0) > 0 && (
                  <span className="text-[11px] text-slate-400" title={`Merged in: ${mergeInfo!.duplicates.map((d) => `#${d.custno}`).join(", ")}`}>
                    +{mergeInfo!.duplicates.length} merged
                  </span>
                )}
            </div>

            {actionsCluster}
          </div>
        </div>
        {/* The tab row — tabs get the WHOLE row (NAV-360-05, Seth: "use the
            whole nav row since on smaller screens the 'notes' is actually
            being cut off"). Each tab stretches to share the full width (Tabs
            bar variant), so all nine fit on a laptop without clipping. */}
        {tabs ? (
          <div className="relative mt-2 border-t border-hairline/60 px-1.5 sm:px-2">{tabs}</div>
        ) : (
          <div className="pb-3" />
        )}
      </div>
    </div>
  );
}

// HeaderContext retired by NAV-360-04: the prose summary + property-
// tenure line moved INTO the band's story line (Seth: "'Recent buyer
// at this property' is kind of floating").

// ----------------------------------------------------------------------------
/**
 * BillingPane (HEADER-RELAYOUT) — ESC's "Billing" tab reborn: WHO pays and
 * WHERE the bill goes. Three honest states: differs (rentals/property
 * managers — the bill-to address + the customer's labeled phones), matches
 * (one quiet line), or not-on-file (most homeowners; also pre-backfill).
 */
export function BillingPane({
  customer,
  location,
  differs,
  compact,
}: {
  customer: CustomerRow;
  location: LocationRow;
  differs: boolean;
  /** 360-B rail: smaller type on the 200px photo. */
  compact?: boolean;
}) {
  void location;
  const phones = [1, 2, 3, 4]
    .map((i) => ({
      phone: (customer as unknown as Record<string, string | null>)[`phone${i}`],
      label: (customer as unknown as Record<string, string | null>)[`lbl_phone${i}`],
    }))
    .filter((p) => p.phone && p.phone.trim());
  const billToName = displayCustomerName(null, customer.fullname);

  // Renders ON the media card's dark scrim — white type, dark-safe chips.
  return (
    <div>
      <div className="mb-1 flex items-center gap-1.5">
        {differs ? (
          <span className="rounded-full bg-amber-400/90 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-950">
            bills at a different address
          </span>
        ) : customer.add1 ? (
          <span className="rounded-full bg-emerald-400/90 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-emerald-950">
            same as service address
          </span>
        ) : (
          <span className="rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white/80 backdrop-blur">
            bill to
          </span>
        )}
      </div>
      <div className={cn(compact ? "text-lg" : "text-2xl", "font-bold leading-tight tracking-tight text-white drop-shadow-sm")}>
        {billToName}
      </div>
      {customer.add1 ? (
        <div className={cn("mt-0.5 leading-snug text-white/85", compact ? "text-[13px]" : "text-[15px]")}>
          {customer.add1}
          {customer.add2 ? `, ${customer.add2}` : ""}
          {customer.city || customer.state ? (
            <>
              {" · "}
              {customer.city}
              {customer.city && customer.state && ", "}
              {customer.state} {customer.zip}
            </>
          ) : null}
        </div>
      ) : (
        <p className="mt-0.5 max-w-md text-xs leading-snug text-white/70">
          No separate billing address on file — invoices go to the service
          address.
        </p>
      )}
      {phones.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
          {phones.map((p) => (
            <a
              key={p.phone}
              href={`tel:${p.phone!.replace(/[^0-9+]/g, "")}`}
              className="flex items-center gap-1.5 text-white/85 transition-colors hover:text-white"
            >
              <Phone className="h-3 w-3 shrink-0 text-white/50" />
              <span className="text-[14px]">{p.phone}</span>
              {p.label && (
                <span className="rounded bg-white/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/80">
                  {p.label}
                </span>
              )}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// IdentityBadge — small derived status pill in the name row (Agreement active,
// Inactive, and future Tier / Property-manager signals). Distinct from
// <StatusBadge> (dispatch status) by design: these are customer-level states.
// ----------------------------------------------------------------------------

type BadgeTone = "slate" | "emerald" | "brand" | "rose" | "amber" | "violet";

/** PROP-01 — add a service property under this customer. Lives in Pegasus
 *  (ESC's own location list stays read-only until cutover). */
export function AddPropertyModal({ custno, onClose }: { custno: string; onClose: () => void }) {
  const add = useAddProperty();
  const [locname, setLocname] = useState("");
  const [add1, setAdd1] = useState("");
  const [city, setCity] = useState("");
  const [statev, setStatev] = useState("MN");
  const [zip, setZip] = useState("");
  const [notes, setNotes] = useState("");
  // DEDUP-AT-ENTRY: is this address already on file anywhere (ESC location,
  // Pegasus property, or a customer's own address)? Warn + jump, never block.
  const { data: addrDupes } = useQuery({
    queryKey: ["prop_addr_dupes", add1],
    enabled: add1.trim().length >= 6,
    staleTime: 15 * 1000,
    queryFn: async () => {
      const t = add1.trim();
      const [{ data: escL }, { data: pegL }, { data: pegC }] = await Promise.all([
        supabase.from("mirror_location").select("custno, locno, add1, city").ilike("add1", `%${t}%`).limit(4),
        supabase.from("pegasus_location").select("custno, locno, add1, city").ilike("add1", `%${t}%`).limit(4),
        supabase.from("pegasus_customer").select("custno, fullname, add1, city").ilike("add1", `%${t}%`).limit(4),
      ]);
      const rows: { custno: string; label: string }[] = [];
      for (const r of (escL ?? []) as { custno: string; add1: string | null; city: string | null }[]) rows.push({ custno: r.custno, label: `${r.add1 ?? ""}${r.city ? ", " + r.city : ""} (ESC)` });
      for (const r of (pegL ?? []) as { custno: string; add1: string | null; city: string | null }[]) rows.push({ custno: r.custno, label: `${r.add1 ?? ""}${r.city ? ", " + r.city : ""} (Pegasus property)` });
      for (const r of (pegC ?? []) as { custno: string; fullname: string | null; add1: string | null; city: string | null }[]) rows.push({ custno: r.custno, label: `${r.add1 ?? ""}${r.city ? ", " + r.city : ""} — ${r.fullname ?? r.custno}` });
      const seen = new Set<string>();
      return rows.filter((r) => { const k = `${r.custno}|${r.label}`; if (seen.has(k)) return false; seen.add(k); return true; }).slice(0, 5);
    },
  });
  const navigate2 = useNavigate();
  const inp = "w-full rounded-md border border-hairline px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20";
  const lbl = "mb-1 block text-[11px] font-medium text-slate-500";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-base font-bold text-slate-900">
            <Building2 className="h-4 w-4 text-brand-600" /> Add a Property
          </h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100"><XIcon className="h-4 w-4" /></button>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          A new service location under this customer — rental, cabin, second building. Saved in Pegasus; ESC isn&apos;t touched.
        </p>
        <div className="mt-3 space-y-2.5">
          <label className="block text-sm"><span className={lbl}>Label (optional — &quot;Rental&quot;, &quot;Lake cabin&quot;)</span><input value={locname} onChange={(e) => setLocname(e.target.value)} className={inp} autoFocus /></label>
          <label className="block text-sm"><span className={lbl}>Street address</span>
            <AddressAutocomplete
              value={add1}
              onChange={setAdd1}
              onResolved={(a) => { setAdd1(a.add1); if (a.city) setCity(a.city); if (a.state) setStatev(a.state); if (a.zip) setZip(a.zip); }}
              className={inp}
            />
          </label>
          <div className="grid grid-cols-3 gap-2.5">
            <label className="block text-sm"><span className={lbl}>City</span><input value={city} onChange={(e) => setCity(e.target.value)} className={inp} /></label>
            <label className="block text-sm"><span className={lbl}>State</span><input value={statev} onChange={(e) => setStatev(e.target.value)} className={inp} /></label>
            <label className="block text-sm"><span className={lbl}>Zip</span><input value={zip} onChange={(e) => setZip(e.target.value)} className={inp} /></label>
          </div>
          <label className="block text-sm"><span className={lbl}>Access notes (optional — gate code, tenant, dog)</span><input value={notes} onChange={(e) => setNotes(e.target.value)} className={inp} /></label>
        </div>
        {addrDupes && addrDupes.length > 0 && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <div className="font-semibold">That address is already in the system:</div>
            <ul className="mt-1 space-y-0.5">
              {addrDupes.map((dd, i) => (
                <li key={i}>
                  <button type="button" onClick={() => { onClose(); navigate2(`/customers/${dd.custno}`); }}
                    className="text-left underline-offset-2 hover:underline">
                    {dd.label}
                  </button>
                </li>
              ))}
            </ul>
            <p className="mt-1 text-[11px] text-amber-700">Open it instead — or if this really is a second unit at the same address, add it anyway.</p>
          </div>
        )}
        {add.isError && <p className="mt-2 text-xs font-medium text-rose-600">Couldn&apos;t add the property — try again.</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100">Cancel</button>
          <button
            type="button"
            disabled={add.isPending || !add1.trim()}
            onClick={() => add.mutate({ custno, locname, add1, city, state: statev, zip, notes }, { onSuccess: onClose })}
            className="rounded-md bg-brand-700 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
          >
            {add.isPending ? "Adding…" : "Add Property"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** PROP-01 — move a Pegasus-added property under a different customer (a
 *  rental sold to a new owner, a building that was filed under the wrong
 *  account). ESC-born properties move at cutover, not here. */
export function MovePropertyModal({ loc, onClose }: { loc: LocationRow; onClose: () => void }) {
  const move = useMoveProperty();
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const [target, setTarget] = useState<{ custno: string; name: string; address: string } | null>(null);
  const [movedTo, setMovedTo] = useState<string | null>(null);
  const { data: hits } = useCustomerLookup(term);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-base font-bold text-slate-900">
            <Building2 className="h-4 w-4 text-brand-600" /> Move This Property
          </h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100"><XIcon className="h-4 w-4" /></button>
        </div>
        {movedTo ? (
          <div className="mt-3 space-y-3 text-center">
            <p className="text-sm text-slate-700">Moved — <b>{loc.add1 ?? loc.locname}</b> now lives under the other customer.</p>
            <button type="button" onClick={() => { onClose(); navigate(`/customers/${movedTo}`); }}
              className="rounded-md bg-brand-700 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-800">
              Open That Customer →
            </button>
          </div>
        ) : (
          <>
            <p className="mt-1 text-xs text-slate-500">
              <b>{loc.add1 ?? loc.locname ?? loc.locno}</b> moves under the customer you pick — its history rides along under the new account.
            </p>
            <input
              value={term}
              onChange={(e) => { setTerm(e.target.value); setTarget(null); }}
              placeholder="Search the new owner by address or building name…"
              autoFocus
              className="mt-3 w-full rounded-md border border-hairline px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
            <ul className="mt-1 max-h-44 divide-y divide-slate-50 overflow-auto">
              {(hits ?? []).map((h) => (
                <li key={`${h.custno}-${h.locno}`}>
                  <button type="button" onClick={() => setTarget({ custno: h.custno, name: h.name ?? h.custno, address: h.address ?? "" })}
                    className={"flex w-full items-baseline gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-brand-50/40 " + (target?.custno === h.custno ? "bg-brand-50" : "")}>
                    <span className="min-w-0 flex-1 truncate font-medium text-slate-800">{h.name}</span>
                    <span className="shrink-0 text-slate-400">{h.address}</span>
                  </button>
                </li>
              ))}
            </ul>
            {move.isError && <p className="mt-2 text-xs font-medium text-rose-600">Couldn&apos;t move it — try again.</p>}
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="rounded-md px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100">Cancel</button>
              <button
                type="button"
                disabled={!target || move.isPending}
                onClick={() => target && move.mutate(
                  { fromCustno: loc.custno, locno: loc.locno, toCustno: target.custno },
                  { onSuccess: () => setMovedTo(target.custno) },
                )}
                className="rounded-md bg-brand-700 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
              >
                {move.isPending ? "Moving…" : target ? `Move Under ${target.name}` : "Pick the New Owner"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * TECH-PREF-CONFIRM (34i82): a badge that came from the notes carries its own
 * answer. Dashed = the extractor's word; tap → the line it came from and
 * Confirm / Not Right (same statuses Watch For's ✓ × write). Solid = confirmed;
 * tap → who and when, and Remove. The extractor never re-proposes a fact that
 * has been answered either way (extract-notes v16).
 */
function SignalBadge({ signal, tone, icon, label, kind, detailText, canCurate }: {
  signal: CustomerSignal;
  tone: BadgeTone;
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  kind: string;
  detailText?: string | null;
  canCurate: boolean;
}) {
  const edit = useEditCustomer(); // EDIT-SUITE-01: Change… opens the suite at Name & Account
  const [open, setOpen] = useState(false);
  const resolve = useResolveSignal();
  const proposed = signal.status !== "confirmed";
  const snippet = stripStars(signal.source_text ?? "");
  const when = signal.confirmed_at
    ? new Date(signal.confirmed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;
  const who = signal.confirmed_by ? signal.confirmed_by.split("@")[0] : null;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  const act = (status: "confirmed" | "dismissed") =>
    resolve.mutate({ id: signal.id, status }, { onSuccess: () => setOpen(false) });
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={proposed ? `${kind} — from the notes, not yet confirmed` : `${kind} — confirmed${who ? ` by ${who}` : ""}${when ? ` · ${when}` : ""}`}
        className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
      >
        <IdentityBadge tone={tone} icon={icon} dashed={proposed}>{label}</IdentityBadge>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div role="dialog" aria-label={kind} className="absolute left-0 top-full z-50 mt-1.5 w-72 rounded-xl border border-hairline bg-white p-3 text-left shadow-e3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-400">{kind}</span>
              <span className={cn("rounded-full px-1.5 py-px text-[10px] font-bold", proposed ? "bg-slate-100 text-slate-600" : "bg-emerald-50 text-emerald-700")}>
                {proposed ? "From the notes" : "Confirmed"}
              </span>
            </div>
            <div className="mt-1 text-[14px] font-bold leading-tight text-slate-900">{label}{detailText ? <span className="font-medium text-slate-500"> · {detailText}</span> : null}</div>
            {snippet && (
              <div className="mt-1.5 rounded-md bg-slate-50 px-2 py-1.5 text-[12px] italic leading-snug text-slate-600">
                “{snippet.slice(0, 180)}{snippet.length > 180 ? "…" : ""}”
              </div>
            )}
            {!proposed && (who || when) && (
              <div className="mt-1.5 text-[11px] text-slate-400">{[who, when].filter(Boolean).join(" · ")}</div>
            )}
            {canCurate && (
              <div className="mt-2.5 flex items-center gap-2">
                {proposed ? (
                  <>
                    <Button size="sm" variant="primary" loading={resolve.isPending} onClick={() => act("confirmed")}>Confirm</Button>
                    <Button size="sm" variant="secondary" disabled={resolve.isPending} onClick={() => act("dismissed")}>Not Right</Button>
                  </>
                ) : (
                  <Button size="sm" variant="secondary" loading={resolve.isPending} onClick={() => act("dismissed")}>Remove</Button>
                )}
                {edit.available && signal.signal_type === "tech_pref" && (
                  <button type="button" onClick={() => { setOpen(false); edit.open("account"); }} className="ml-auto text-[11.5px] font-bold text-brand-700 hover:underline">Change…</button>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </span>
  );
}

function IdentityBadge({
  tone,
  icon: Icon,
  dashed,
  children,
}: {
  tone: BadgeTone;
  icon?: React.ComponentType<{ className?: string }>;
  /** HDR-DISPATCH-01: a fact that came from the notes and is not yet confirmed. */
  dashed?: boolean;
  children: React.ReactNode;
}) {
  const tones: Record<BadgeTone, string> = {
    slate: "bg-slate-100 text-slate-600",
    emerald: "bg-emerald-50 text-emerald-700",
    brand: "bg-brand-50 text-brand-700",
    rose: "bg-rose-100 text-rose-700",
    // NAV-360-05: the Recent-buyer / special-note badge next to Member.
    amber: "bg-amber-50 text-amber-800",
    // HDR-DISPATCH-01: the stated tech preference.
    violet: "bg-violet-50 text-violet-700",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        tones[tone],
        dashed && "bg-white ring-1 ring-inset ring-dashed",
        dashed && (tone === "amber" ? "ring-amber-300" : tone === "emerald" ? "ring-emerald-300" : tone === "rose" ? "ring-rose-300" : tone === "slate" ? "ring-slate-300" : "ring-violet-300"),
      )}
    >
      {Icon && <Icon className="h-3 w-3" />}
      {children}
    </span>
  );
}

// HeaderActions retired by NAV-360-03 (Direction B): Book a Call is the one
// primary on the band; Quote and Note live in the ··· menu's Create section.

